// CON-491 — the contact rule the contact drawer applies before it saves (D-018 item 8).
//
// A contact needs an email OR a phone; the name is optional. A POS donor can be
// phone-only and nameless, and staff must be able to correct that contact here
// without inventing a name. This mirrors the contacts-db-backend rule
// (contact_creator / contact_editor, `contact_channel_required`):
//
//   - create: refused with no usable email and no usable phone;
//   - edit:   refused when it removes the contact's last email and last phone;
//   - edit of a contact that never had either (a name-only contact created
//     before the rule): still saves, so long as it keeps a name.
//
// The backend is the authority; this check spares staff a round trip and keeps
// the drawer and the backend giving the same answer.

export const CONTACT_CHANNEL_REQUIRED_MESSAGE = 'A contact needs an email or a phone number.';

export const CONTACT_CHANNEL_REQUIRED_ERROR = 'contact_channel_required';

interface ChannelEntry {
  value?: string | null;
  email?: string | null;
  deleted?: boolean;
}

interface ContactChannels {
  firstName?: string | null;
  lastName?: string | null;
  emails?: ChannelEntry[] | null;
  phones?: ChannelEntry[] | null;
  // Pre-array stored rows can still carry the legacy scalars.
  email?: string | null;
  phoneNumber?: string | null;
}

interface ContactChannelRuleInput {
  isEditMode: boolean;
  form: ContactChannels;
  /** The stored contact being edited (the drawer's `contact` prop). */
  original?: ContactChannels | null;
  isUsableEmail: (value: string) => boolean;
  isUsablePhone: (value: string) => boolean;
}

// A blank, whitespace-only or stored 'None'/'null' value is missing — the same
// cleaning the backend's _clean_name_part applies.
const cleanText = (value: unknown): string => {
  if (typeof value !== 'string') return '';
  const text = value.trim();
  return text === 'None' || text === 'null' ? '' : text;
};

const liveEntries = (entries?: ChannelEntry[] | null): ChannelEntry[] =>
  (entries ?? []).filter((entry) => entry && !entry.deleted);

const hasChannel = (
  contact: ContactChannels,
  isUsableEmail: (value: string) => boolean,
  isUsablePhone: (value: string) => boolean,
): boolean => {
  const emailUsable = (raw: unknown) => {
    const text = cleanText(raw);
    return text !== '' && isUsableEmail(text);
  };
  const phoneUsable = (raw: unknown) => {
    const text = cleanText(raw);
    return text !== '' && isUsablePhone(text);
  };
  return (
    liveEntries(contact.emails).some((e) => emailUsable(e.value) || emailUsable(e.email)) ||
    liveEntries(contact.phones).some((p) => phoneUsable(p.value)) ||
    emailUsable(contact.email) ||
    phoneUsable(contact.phoneNumber)
  );
};

/** True when the save must be refused because the contact would have no channel. */
export function violatesContactChannelRule({
  isEditMode,
  form,
  original,
  isUsableEmail,
  isUsablePhone,
}: ContactChannelRuleInput): boolean {
  // The form's own arrays only; the legacy scalars are never sent by the drawer.
  const formChannels = { emails: form.emails, phones: form.phones };
  if (hasChannel(formChannels, isUsableEmail, isUsablePhone)) return false;
  if (!isEditMode) return true;
  // The edit removes the last email and the last phone.
  if (original && hasChannel(original, isUsableEmail, isUsablePhone)) return true;
  // A name-only contact may keep saving, but not as an empty row.
  return !cleanText(form.firstName) && !cleanText(form.lastName);
}
